import _isEmpty from 'lodash/isEmpty';
import type {
  IWithdrawScreenData,
  WithdrawMode,
} from '../../interfaces/Withdrawal';
import type { IUserDetails } from '../../storage/store';
import { EBankStatus } from '../../storage/store';

export const DEFAULT_UPI_REGEX = '([\\w.-]*[@][\\w]*)';

/**
 * Android caches the withdrawal session for `cacheTTL` from `timeStamp` and
 * re-initialises once it lapses, rather than letting the server reject a
 * submit the user has already confirmed (`isWithdrawScreenDataValid`).
 */
export function isSessionValid(
  screenData?: IWithdrawScreenData,
  sessionTimestamp?: number,
): boolean {
  const sessionId = screenData?.screenData?.sessionId;
  if (_isEmpty(sessionId)) return false;

  const ttl = screenData?.screenData?.cacheTTL;
  const issuedAt = screenData?.screenData?.timeStamp ?? sessionTimestamp;
  // No TTL from the server means the session does not expire client-side.
  if (!ttl || !issuedAt) return true;

  return Date.now() - issuedAt < ttl;
}

export interface IAmountLimits {
  min?: number;
  max?: number;
  balance?: number;
}

export type TAmountErrorKey =
  | 'minWithdrawError'
  | 'maxWithdrawError'
  | 'insufficientBalanceError'
  | null;

/**
 * Returns the translation key for the amount error, or null when valid.
 * Mirrors the ordering in `WithdrawalFragment.setWithdrawError`: balance is
 * checked before the configured maximum, since it is the harder limit.
 */
export function getAmountErrorKey(
  amount: number | undefined,
  { min, max, balance }: IAmountLimits,
): TAmountErrorKey {
  if (amount === undefined || Number.isNaN(amount) || amount <= 0) return null;

  if (balance !== undefined && amount > Math.floor(balance)) {
    return 'insufficientBalanceError';
  }
  if (min !== undefined && amount < min) return 'minWithdrawError';
  if (max !== undefined && amount > max) return 'maxWithdrawError';

  return null;
}

/** Resolves the effective limits — the section's own values win over the screen's. */
export function getAmountLimits(
  screenData?: IWithdrawScreenData,
): IAmountLimits {
  const screen = screenData?.screenData;
  const payout = screenData?.withdrawData;

  return {
    min: payout?.minUserInput ?? screen?.minUserInput,
    max: payout?.maxUserInput ?? screen?.maxUserInput,
    balance: screen?.balance,
  };
}

/**
 * `amountInRupees` is an Int on the wire while `actualCoins` is a Float —
 * keep whole rupees everywhere so a float never reaches a request body.
 */
export function parseAmountInput(raw: string): number | undefined {
  const digitsOnly = raw.replace(/[^0-9]/g, '');
  if (digitsOnly === '') return undefined;
  return parseInt(digitsOnly, 10);
}

export function formatRupees(value?: number): string {
  if (value === undefined || Number.isNaN(value)) return '₹0';
  return `₹${Math.floor(value).toLocaleString('en-IN')}`;
}

/**
 * Which payout modes are actually usable.
 *
 * `modeData` carries no `upiData` / `bankData` in the live response, so mode
 * availability comes from the same two sources Android uses
 * (`WithdrawalViewModel.isUpiAvailable` / `isBankAvailable`): the server's
 * `withdrawalOptions` intersected with what the user actually has saved.
 *
 * These can legitimately be disjoint — the server may offer BANK only while the
 * user has only a UPI, in which case there is no usable mode and one must be
 * added.
 */
export function getAvailableModes(
  screenData?: IWithdrawScreenData,
  userDetails?: IUserDetails,
): WithdrawMode[] {
  const options = screenData?.withdrawalOptions ?? [];
  const modes: WithdrawMode[] = [];

  if (options.includes('UPI') && !_isEmpty(userDetails?.upiId)) {
    modes.push('UPI');
  }

  const bankStatus = userDetails?.bankDetails?.status;
  const hasBank =
    bankStatus === EBankStatus.SUCCESS || bankStatus === EBankStatus.PENDING;
  if (options.includes('BANK') && hasBank) {
    modes.push('BANK');
  }
  return modes;
}

/** The modes the server is willing to accept, saved or not. Drives add-mode. */
export function getOfferedModes(
  screenData?: IWithdrawScreenData,
): WithdrawMode[] {
  const options = screenData?.withdrawalOptions ?? [];
  return (['UPI', 'BANK'] as WithdrawMode[]).filter((mode) =>
    options.includes(mode),
  );
}

export function getDefaultMode(
  screenData?: IWithdrawScreenData,
  userDetails?: IUserDetails,
): WithdrawMode | undefined {
  const available = getAvailableModes(screenData, userDetails);
  if (_isEmpty(available)) return undefined;

  const recommended = screenData?.withdrawData?.modeData?.recommendedMode;
  const match = available.find((mode) => mode === recommended);

  return match ?? available[0];
}

/** Masked so the full account number never renders. */
function maskAccountNumber(accountNumber?: string): string {
  if (!accountNumber) return '';
  const last4 = accountNumber.slice(-4);
  return `••••${last4}`;
}

export function getModeDisplay(
  userDetails?: IUserDetails,
  mode?: WithdrawMode,
): { value?: string } {
  if (mode === 'BANK') {
    const bank = userDetails?.bankDetails;
    if (!bank) return {};
    return {
      value: [bank.bankName, maskAccountNumber(bank.accountNumber)]
        .filter(Boolean)
        .join(' '),
    };
  }
  return { value: userDetails?.upiId };
}

/** Whether the section is usable at all — used for the breakdown request flags. */
export function isWithdrawAvailable(screenData?: IWithdrawScreenData): boolean {
  return !_isEmpty(screenData?.withdrawData);
}
